/**
 * Guide + GuideCategory API hooks. Maps to apps/guides/views.py.
 *
 * No draft/publish lifecycle here (unlike flows.ts) — a saved entry is
 * visible to Staff immediately, so a create/update only needs to
 * invalidate the list query, nothing else.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import type {
  GuideCategory,
  GuideCategoryInput,
  GuideEntry,
  GuideEntryInput,
  GuideNoteMap,
  Paginated,
} from "@/types/api";

const GUIDE_CATEGORIES_KEY = ["guide-categories"];
const GUIDE_ENTRIES_KEY = ["guide-entries"];
const GUIDE_NOTES_KEY = ["guide-notes"];

// ---------- Categories ----------

export function useGuideCategories() {
  return useQuery<Paginated<GuideCategory>>({
    queryKey: GUIDE_CATEGORIES_KEY,
    queryFn: async () => {
      const r = await api.get<Paginated<GuideCategory>>("/v1/guides/categories/");
      return r.data;
    },
  });
}

export function useCreateGuideCategory() {
  const queryClient = useQueryClient();
  return useMutation<GuideCategory, unknown, GuideCategoryInput>({
    mutationFn: async (input) => {
      const r = await api.post<GuideCategory>("/v1/guides/categories/", input);
      return r.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: GUIDE_CATEGORIES_KEY }),
  });
}

// ---------- Entries ----------

// Fetches every entry in one request — GuideListPage filters by category,
// search, and entry_type (Guideline/Rebuttal — see types/api.ts) all
// client-side against this same list, same as it already did for category
// before entry_type existed. Kept as one shared query (not one per filter
// combination) so switching tabs is instant, no refetch/loading flicker.
export function useGuideEntries() {
  return useQuery<Paginated<GuideEntry>>({
    queryKey: GUIDE_ENTRIES_KEY,
    queryFn: async () => {
      const r = await api.get<Paginated<GuideEntry>>("/v1/guides/");
      return r.data;
    },
  });
}

export function useCreateGuideEntry() {
  const queryClient = useQueryClient();
  return useMutation<GuideEntry, unknown, GuideEntryInput>({
    mutationFn: async (input) => {
      const r = await api.post<GuideEntry>("/v1/guides/", input);
      return r.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: GUIDE_ENTRIES_KEY }),
  });
}

export function useUpdateGuideEntry() {
  const queryClient = useQueryClient();
  return useMutation<GuideEntry, unknown, { id: string; input: GuideEntryInput }>({
    mutationFn: async ({ id, input }) => {
      const r = await api.put<GuideEntry>(`/v1/guides/${id}/`, input);
      return r.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: GUIDE_ENTRIES_KEY }),
  });
}

export function useDeleteGuideEntry() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: async (id) => {
      await api.delete(`/v1/guides/${id}/`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: GUIDE_ENTRIES_KEY }),
  });
}

// ---------- Staff private notes ("Notes & Links") — see
// apps.guides.views.GuideEntryViewSet.notes/note and GuideNoteMap. ----------

export function useGuideNotes() {
  return useQuery<GuideNoteMap>({
    queryKey: GUIDE_NOTES_KEY,
    queryFn: async () => {
      const r = await api.get<GuideNoteMap>("/v1/guides/notes/");
      return r.data;
    },
  });
}

export function useSaveGuideNote() {
  const queryClient = useQueryClient();
  return useMutation<string | null, unknown, { entryId: string; note: string }>({
    mutationFn: async ({ entryId, note }) => {
      // Blank in the box is the same as "no note" — the API deletes the
      // row and returns 204 rather than a body, same convention as node
      // personalization.
      const r = await api.put<{ note: string }>(`/v1/guides/${entryId}/note/`, { note });
      return r.status === 204 ? null : r.data.note;
    },
    onSuccess: (note, { entryId }) => {
      queryClient.setQueryData<GuideNoteMap>(GUIDE_NOTES_KEY, (prev) => {
        const next = { ...(prev ?? {}) };
        if (note != null) {
          next[entryId] = note;
        } else {
          delete next[entryId];
        }
        return next;
      });
    },
  });
}
